import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { api } from "../../utils/apiClient";
import { API_ENDPOINTS } from "../../config/api";
import Sidebar from "../components/Sidebar";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Alert from "../../components/common/Alert";
import {
  ClipboardList,
  CheckCircle,
  PieChart,
  Grid,
  DollarSign,
  Search,
  TrendingUp,
  TrendingDown,
  Users,
  Building,
  Calendar,
  AlertTriangle,
  RefreshCw,
  Eye,
  BarChart3,
  Activity,
} from "lucide-react";

export default function ProjectsDashboard() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  // Enhanced metrics with more comprehensive data
  const [completedSalesAwaiting, setCompletedSalesAwaiting] = useState(25);
  const [completedSalesDischarged, setCompletedSalesDischarged] = useState(40);
  const [leadFilesCompletion, setLeadFilesCompletion] = useState(70); // percentage
  const [plotsAvailable, setPlotsAvailable] = useState(120);
  const [stockValue, setStockValue] = useState(450000); // total stock value
  const [totalProjects, setTotalProjects] = useState(0);
  const [activeProjects, setActiveProjects] = useState(0);
  const [completedProjects, setCompletedProjects] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [monthlyGrowth, setMonthlyGrowth] = useState(12.5); // percentage

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const department = useSelector((state) => state.user.user.department);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get(API_ENDPOINTS.PROJECTS.LIST);

        if (response.data && Array.isArray(response.data.projects)) {
          const projectsData = response.data.projects;
          setProjects(projectsData);
          setFilteredProjects(projectsData);
          setTotalProjects(projectsData.length);
          setActiveProjects(
            projectsData.filter((p) => p.status === "active").length
          );
          setCompletedProjects(
            projectsData.filter((p) => p.status === "completed").length
          );
        } else {
          setError("Unexpected data format received from server.");
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        setError(
          err.message || "Failed to load projects. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await api.get(API_ENDPOINTS.PROJECTS.LIST);
      if (response.data && Array.isArray(response.data.projects)) {
        const projectsData = response.data.projects;
        setProjects(projectsData);
        setFilteredProjects(projectsData);
        setTotalProjects(projectsData.length);
        setActiveProjects(
          projectsData.filter((p) => p.status === "active").length
        );
        setCompletedProjects(
          projectsData.filter((p) => p.status === "completed").length
        );
      }
    } catch (err) {
      console.error("Error refreshing projects:", err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let filtered = [...projects];

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.Project_Name.toLowerCase().includes(q) ||
          (p.description && p.description.toLowerCase().includes(q))
      );
    }

    // Sort filter
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case "name":
          aVal = a.Project_Name;
          bVal = b.Project_Name;
          break;
        case "status":
          aVal = a.status || "active";
          bVal = b.status || "active";
          break;
        default:
          return 0;
      }

      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredProjects(filtered);
  }, [searchQuery, projects, sortBy, sortOrder]);

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-lg font-medium text-gray-700">
              Loading dashboard...
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Please wait while we fetch the latest data
            </p>
          </div>
        </div>
      </Sidebar>
    );
  }

  if (error) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              Error Loading Dashboard
            </h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  const metrics = [
    {
      title: "Total Projects",
      value: totalProjects,
      icon: Building,
      bg: "bg-blue-100",
      color: "text-blue-500",
      trend: "+5%",
      trendUp: true,
    },
    {
      title: "Active Projects",
      value: activeProjects,
      icon: Activity,
      bg: "bg-green-100",
      color: "text-green-500",
      trend: "+12%",
      trendUp: true,
    },
    {
      title: "Completed Sales Awaiting",
      value: completedSalesAwaiting,
      icon: ClipboardList,
      bg: "bg-orange-100",
      color: "text-orange-500",
      trend: "-3%",
      trendUp: false,
    },
    {
      title: "Completed Sales Discharged",
      value: completedSalesDischarged,
      icon: CheckCircle,
      bg: "bg-green-100",
      color: "text-green-500",
      trend: "+8%",
      trendUp: true,
    },
    {
      title: "Lead Files Completion",
      value: `${leadFilesCompletion}%`,
      icon: PieChart,
      bg: "bg-blue-100",
      color: "text-blue-500",
      isProgress: true,
      trend: "+2%",
      trendUp: true,
    },
    {
      title: "Plots Available",
      value: plotsAvailable,
      icon: Grid,
      bg: "bg-purple-100",
      color: "text-purple-500",
      trend: "+15%",
      trendUp: true,
    },
    {
      title: "Stock Value",
      value: `Ksh. ${stockValue.toLocaleString()}`,
      icon: DollarSign,
      bg: "bg-teal-100",
      color: "text-teal-500",
      trend: "+18%",
      trendUp: true,
    },
    {
      title: "Monthly Growth",
      value: `${monthlyGrowth}%`,
      icon: TrendingUp,
      bg: "bg-indigo-100",
      color: "text-indigo-500",
      trend: "+5%",
      trendUp: true,
    },
  ];

  return (
    <Sidebar>
      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Project Dashboard
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Comprehensive overview of projects and performance metrics
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${
                      refreshing ? "animate-spin" : ""
                    }`}
                  />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Metrics Cards */}
        <div className="px-6 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metrics.map((m) => (
              <div
                key={m.title}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`${m.bg} p-3 rounded-full`}>
                      <m.icon className={`${m.color} w-6 h-6`} />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">
                        {m.title}
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {m.value}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className={`flex items-center text-sm font-medium ${
                        m.trendUp ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {m.trendUp ? (
                        <TrendingUp className="h-4 w-4 mr-1" />
                      ) : (
                        <TrendingDown className="h-4 w-4 mr-1" />
                      )}
                      {m.trend}
                    </div>
                  </div>
                </div>
                {m.isProgress && (
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${leadFilesCompletion}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {leadFilesCompletion}% completion rate
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Search and Filter Section */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Search projects by name or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="name">Sort by Name</option>
                    <option value="status">Sort by Status</option>
                  </select>
                  <button
                    onClick={() =>
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                    }
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {sortOrder === "asc" ? (
                      <TrendingUp className="h-4 w-4" />
                    ) : (
                      <TrendingDown className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Projects Table */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                All Projects ({filteredProjects.length})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProjects.length > 0 ? (
                    filteredProjects.map((project) => (
                      <tr
                        key={project.Project_Name}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <Building className="h-5 w-5 text-blue-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {project.Project_Name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {project.description ||
                                  "No description available"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              project.status === "active"
                                ? "bg-green-100 text-green-800"
                                : project.status === "completed"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {project.status || "Active"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Link
                              to={`/projects/${encodeURIComponent(
                                project.Project_Name
                              )}/plots`}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View Plots
                            </Link>
                            <Link
                              to={`/projects/${encodeURIComponent(
                                project.Project_Name
                              )}/transactions`}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                              <BarChart3 className="h-3 w-3 mr-1" />
                              Transactions
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="px-6 py-12 text-center">
                        <Building className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">
                          No projects found
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {searchQuery
                            ? "No projects match your search criteria."
                            : "No projects available."}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Sidebar>
  );
}
